import React, { useState, useEffect } from 'react';
import { format, addDays, startOfMonth, endOfMonth, isWeekend, isBefore, startOfDay } from 'date-fns';
import { de } from 'date-fns/locale';

// German month and day names for calendar
const germanMonths = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
];
const germanWeekdaysShort = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Check, Loader2, Phone, Mail, User, MapPin, Building, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { id: 1, title: 'Persönliche Daten', icon: User },
  { id: 2, title: 'Pflege-Details', icon: Building },
  { id: 3, title: 'Terminwahl', icon: CalendarIcon },
  { id: 4, title: 'Bestätigung', icon: Check }
];

const CARE_LEVELS = [
  { value: 'Pflegegrad 1', label: 'Pflegegrad 1' },
  { value: 'Pflegegrad 2', label: 'Pflegegrad 2' },
  { value: 'Pflegegrad 3', label: 'Pflegegrad 3' },
  { value: 'Pflegegrad 4', label: 'Pflegegrad 4' },
  { value: 'Pflegegrad 5', label: 'Pflegegrad 5' },
];

const BookingWizard = ({ isOpen, onOpenChange }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [bookingComplete, setBookingComplete] = useState(false);
  const [bookingData, setBookingData] = useState(null);
  
  // Available slots state
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    street: '',
    zipCode: '',
    city: '',
    birthDay: '',
    birthMonth: '',
    birthYear: '',
    careLevel: '',
    insuranceName: '',
    insuranceNumber: '',
    phone: '',
    email: '',
    appointmentDate: null,
    appointmentTime: ''
  });

  // Form errors
  const [errors, setErrors] = useState({});

  // Fetch available slots when date changes
  useEffect(() => {
    if (formData.appointmentDate) {
      fetchAvailableSlots(formData.appointmentDate);
    }
  }, [formData.appointmentDate]);

  const fetchAvailableSlots = async (date) => {
    setLoadingSlots(true);
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      const response = await axios.get(`${API_URL}/api/available-slots`, {
        params: { date_str: dateStr }
      });
      setAvailableSlots(response.data.slots || []);
    } catch (error) {
      console.error('Error fetching slots:', error);
      toast.error('Fehler beim Laden der verfügbaren Zeiten');
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when field is updated
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData.firstName.trim()) newErrors.firstName = 'Vorname ist erforderlich';
      if (!formData.lastName.trim()) newErrors.lastName = 'Nachname ist erforderlich';
      if (!formData.street.trim()) newErrors.street = 'Straße ist erforderlich';
      if (!formData.zipCode.trim()) newErrors.zipCode = 'PLZ ist erforderlich';
      if (!formData.city.trim()) newErrors.city = 'Stadt ist erforderlich';
      if (!formData.birthDay || !formData.birthMonth || !formData.birthYear) newErrors.birthDate = 'Geburtsdatum ist erforderlich';
    }

    if (step === 2) {
      if (!formData.careLevel) newErrors.careLevel = 'Pflegegrad ist erforderlich';
      if (!formData.insuranceName.trim()) newErrors.insuranceName = 'Krankenkasse ist erforderlich';
      if (!formData.insuranceNumber.trim()) newErrors.insuranceNumber = 'Versicherungsnummer ist erforderlich';
      if (!formData.phone.trim()) newErrors.phone = 'Telefonnummer ist erforderlich';
      if (!formData.email.trim()) {
        newErrors.email = 'E-Mail ist erforderlich';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Ungültige E-Mail-Adresse';
      }
    }

    if (step === 3) {
      if (!formData.appointmentDate) newErrors.appointmentDate = 'Datum ist erforderlich';
      if (!formData.appointmentTime) newErrors.appointmentTime = 'Uhrzeit ist erforderlich';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) return;

    setIsLoading(true);
    try {
      // Format birth date from separate fields
      const birthDateStr = `${formData.birthYear}-${formData.birthMonth.padStart(2, '0')}-${formData.birthDay.padStart(2, '0')}`;
      
      const payload = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        street: formData.street,
        zip_code: formData.zipCode,
        city: formData.city,
        birth_date: birthDateStr,
        care_level: formData.careLevel,
        insurance_name: formData.insuranceName,
        insurance_number: formData.insuranceNumber,
        phone: formData.phone,
        email: formData.email,
        appointment_date: format(formData.appointmentDate, 'yyyy-MM-dd'),
        appointment_time: formData.appointmentTime
      };

      const response = await axios.post(`${API_URL}/api/bookings`, payload);
      setBookingData(response.data);
      setBookingComplete(true);
      setCurrentStep(4);
      toast.success('Termin erfolgreich gebucht!');
    } catch (error) {
      console.error('Booking error:', error);
      const message = error.response?.data?.detail || 'Fehler bei der Terminbuchung';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      street: '',
      zipCode: '',
      city: '',
      birthDay: '',
      birthMonth: '',
      birthYear: '',
      careLevel: '',
      insuranceName: '',
      insuranceNumber: '',
      phone: '',
      email: '',
      appointmentDate: null,
      appointmentTime: ''
    });
    setErrors({});
    setCurrentStep(1);
    setBookingComplete(false);
    setBookingData(null);
  };

  // Disable weekends and past dates
  const disabledDays = (date) => {
    return isWeekend(date) || isBefore(date, startOfDay(new Date()));
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl overflow-hidden max-w-4xl mx-auto" data-testid="booking-wizard">
      {/* Progress Steps */}
      <div className="bg-slate-50 p-6 border-b border-slate-100">
        <div className="flex justify-between items-center">
          {STEPS.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <div className={`
                flex items-center justify-center w-10 h-10 rounded-full font-bold text-sm
                transition-all duration-300
                ${currentStep > step.id 
                  ? 'bg-green-500 text-white' 
                  : currentStep === step.id 
                    ? 'bg-[#40E0D0] text-slate-900' 
                    : 'bg-slate-200 text-slate-500'}
              `}>
                {currentStep > step.id ? <Check className="w-5 h-5" /> : step.id}
              </div>
              <span className={`
                ml-3 text-sm font-medium hidden sm:block
                ${currentStep === step.id ? 'text-slate-900' : 'text-slate-500'}
              `}>
                {step.title}
              </span>
              {index < STEPS.length - 1 && (
                <div className={`
                  w-8 md:w-16 h-0.5 mx-2 md:mx-4
                  ${currentStep > step.id ? 'bg-green-500' : 'bg-slate-200'}
                `} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Form Content */}
      <div className="p-6 md:p-8">
        {/* Step 1: Personal Data */}
        {currentStep === 1 && (
          <div className="form-section space-y-6" data-testid="step-personal">
            <h3 className="font-['Merriweather'] text-xl font-bold text-slate-900 mb-6">
              Persönliche Daten
            </h3>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label className="text-slate-700 font-semibold text-base mb-2 block">
                  Vorname *
                </Label>
                <Input
                  value={formData.firstName}
                  onChange={(e) => updateFormData('firstName', e.target.value)}
                  placeholder="Max"
                  className={`h-12 text-lg ${errors.firstName ? 'border-red-500' : ''}`}
                  data-testid="input-firstname"
                />
                {errors.firstName && <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>}
              </div>

              <div>
                <Label className="text-slate-700 font-semibold text-base mb-2 block">
                  Nachname *
                </Label>
                <Input
                  value={formData.lastName}
                  onChange={(e) => updateFormData('lastName', e.target.value)}
                  placeholder="Mustermann"
                  className={`h-12 text-lg ${errors.lastName ? 'border-red-500' : ''}`}
                  data-testid="input-lastname"
                />
                {errors.lastName && <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>}
              </div>
            </div>

            <div>
              <Label className="text-slate-700 font-semibold text-base mb-2 block">
                Straße und Hausnummer *
              </Label>
              <Input
                value={formData.street}
                onChange={(e) => updateFormData('street', e.target.value)}
                placeholder="Musterstraße 123"
                className={`h-12 text-lg ${errors.street ? 'border-red-500' : ''}`}
                data-testid="input-street"
              />
              {errors.street && <p className="text-red-500 text-sm mt-1">{errors.street}</p>}
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label className="text-slate-700 font-semibold text-base mb-2 block">
                  PLZ *
                </Label>
                <Input
                  value={formData.zipCode}
                  onChange={(e) => updateFormData('zipCode', e.target.value)}
                  placeholder="04229"
                  className={`h-12 text-lg ${errors.zipCode ? 'border-red-500' : ''}`}
                  data-testid="input-zipcode"
                />
                {errors.zipCode && <p className="text-red-500 text-sm mt-1">{errors.zipCode}</p>}
              </div>

              <div>
                <Label className="text-slate-700 font-semibold text-base mb-2 block">
                  Stadt *
                </Label>
                <Input
                  value={formData.city}
                  onChange={(e) => updateFormData('city', e.target.value)}
                  placeholder="Leipzig"
                  className={`h-12 text-lg ${errors.city ? 'border-red-500' : ''}`}
                  data-testid="input-city"
                />
                {errors.city && <p className="text-red-500 text-sm mt-1">{errors.city}</p>}
              </div>
            </div>

            <div>
              <Label className="text-slate-700 font-semibold text-base mb-2 block">
                Geburtsdatum *
              </Label>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Select 
                    value={formData.birthDay || ''} 
                    onValueChange={(value) => updateFormData('birthDay', value)}
                  >
                    <SelectTrigger 
                      className={`h-12 text-lg ${errors.birthDate ? 'border-red-500' : ''}`}
                      data-testid="select-birthday"
                    >
                      <SelectValue placeholder="Tag" />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                        <SelectItem key={day} value={day.toString()} className="text-lg">
                          {day}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Select 
                    value={formData.birthMonth || ''} 
                    onValueChange={(value) => updateFormData('birthMonth', value)}
                  >
                    <SelectTrigger 
                      className={`h-12 text-lg ${errors.birthDate ? 'border-red-500' : ''}`}
                      data-testid="select-birthmonth"
                    >
                      <SelectValue placeholder="Monat" />
                    </SelectTrigger>
                    <SelectContent>
                      {[
                        { value: '1', label: 'Januar' },
                        { value: '2', label: 'Februar' },
                        { value: '3', label: 'März' },
                        { value: '4', label: 'April' },
                        { value: '5', label: 'Mai' },
                        { value: '6', label: 'Juni' },
                        { value: '7', label: 'Juli' },
                        { value: '8', label: 'August' },
                        { value: '9', label: 'September' },
                        { value: '10', label: 'Oktober' },
                        { value: '11', label: 'November' },
                        { value: '12', label: 'Dezember' }
                      ].map((month) => (
                        <SelectItem key={month.value} value={month.value} className="text-lg">
                          {month.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Select 
                    value={formData.birthYear || ''} 
                    onValueChange={(value) => updateFormData('birthYear', value)}
                  >
                    <SelectTrigger 
                      className={`h-12 text-lg ${errors.birthDate ? 'border-red-500' : ''}`}
                      data-testid="select-birthyear"
                    >
                      <SelectValue placeholder="Jahr" />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 100 }, (_, i) => new Date().getFullYear() - i).map((year) => (
                        <SelectItem key={year} value={year.toString()} className="text-lg">
                          {year}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {errors.birthDate && <p className="text-red-500 text-sm mt-1">{errors.birthDate}</p>}
            </div>
          </div>
        )}

        {/* Step 2: Care Details */}
        {currentStep === 2 && (
          <div className="form-section space-y-6" data-testid="step-care">
            <h3 className="font-['Merriweather'] text-xl font-bold text-slate-900 mb-6">
              Pflege-Details & Kontakt
            </h3>

            <div>
              <Label className="text-slate-700 font-semibold text-base mb-2 block">
                Pflegegrad *
              </Label>
              <Select 
                value={formData.careLevel} 
                onValueChange={(value) => updateFormData('careLevel', value)}
              >
                <SelectTrigger 
                  className={`h-12 text-lg ${errors.careLevel ? 'border-red-500' : ''}`}
                  data-testid="select-carelevel"
                >
                  <SelectValue placeholder="Pflegegrad auswählen" />
                </SelectTrigger>
                <SelectContent>
                  {CARE_LEVELS.map((level) => (
                    <SelectItem key={level.value} value={level.value} className="text-lg">
                      {level.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.careLevel && <p className="text-red-500 text-sm mt-1">{errors.careLevel}</p>}
            </div>

            <div>
              <Label className="text-slate-700 font-semibold text-base mb-2 block">
                Name der Krankenkasse *
              </Label>
              <Input
                value={formData.insuranceName}
                onChange={(e) => updateFormData('insuranceName', e.target.value)}
                placeholder="z.B. AOK Plus"
                className={`h-12 text-lg ${errors.insuranceName ? 'border-red-500' : ''}`}
                data-testid="input-insurance"
              />
              {errors.insuranceName && <p className="text-red-500 text-sm mt-1">{errors.insuranceName}</p>}
            </div>

            <div>
              <Label className="text-slate-700 font-semibold text-base mb-2 block">
                Versicherungsnummer *
              </Label>
              <Input
                value={formData.insuranceNumber}
                onChange={(e) => updateFormData('insuranceNumber', e.target.value)}
                placeholder="A123456789"
                className={`h-12 text-lg ${errors.insuranceNumber ? 'border-red-500' : ''}`}
                data-testid="input-insurancenumber"
              />
              {errors.insuranceNumber && <p className="text-red-500 text-sm mt-1">{errors.insuranceNumber}</p>}
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label className="text-slate-700 font-semibold text-base mb-2 block">
                  <Phone className="w-4 h-4 inline mr-2" />
                  Telefonnummer für Rückruf *
                </Label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => updateFormData('phone', e.target.value)}
                  placeholder="0151 12345678"
                  className={`h-12 text-lg ${errors.phone ? 'border-red-500' : ''}`}
                  data-testid="input-phone"
                />
                {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
              </div>

              <div>
                <Label className="text-slate-700 font-semibold text-base mb-2 block">
                  <Mail className="w-4 h-4 inline mr-2" />
                  E-Mail-Adresse *
                </Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => updateFormData('email', e.target.value)}
                  placeholder="max@beispiel.de"
                  className={`h-12 text-lg ${errors.email ? 'border-red-500' : ''}`}
                  data-testid="input-email"
                />
                {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Appointment Selection */}
        {currentStep === 3 && (
          <div className="form-section space-y-6" data-testid="step-appointment">
            <h3 className="font-['Merriweather'] text-xl font-bold text-slate-900 mb-6">
              Termin auswählen
            </h3>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Calendar */}
              <div>
                <Label className="text-slate-700 font-semibold text-base mb-3 block">
                  Datum wählen *
                </Label>
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <Calendar
                    mode="single"
                    selected={formData.appointmentDate}
                    onSelect={(date) => {
                      updateFormData('appointmentDate', date);
                      updateFormData('appointmentTime', ''); // Reset time on date change
                    }}
                    disabled={disabledDays}
                    locale={de}
                    className="rounded-md"
                    data-testid="calendar-appointment"
                  />
                </div>
                {errors.appointmentDate && <p className="text-red-500 text-sm mt-1">{errors.appointmentDate}</p>}
              </div>

              {/* Time Slots */}
              <div>
                <Label className="text-slate-700 font-semibold text-base mb-3 block">
                  Uhrzeit wählen *
                </Label>
                
                {!formData.appointmentDate ? (
                  <div className="text-center py-12 bg-slate-50 rounded-xl border border-slate-200">
                    <CalendarIcon className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">Bitte wählen Sie zuerst ein Datum</p>
                  </div>
                ) : loadingSlots ? (
                  <div className="text-center py-12 bg-slate-50 rounded-xl border border-slate-200">
                    <Loader2 className="w-8 h-8 text-[#40E0D0] mx-auto animate-spin mb-3" />
                    <p className="text-slate-500">Lade verfügbare Zeiten...</p>
                  </div>
                ) : (
                  <div className="bg-slate-50 rounded-xl border border-slate-200 p-4 max-h-80 overflow-y-auto">
                    <div className="grid grid-cols-3 gap-2">
                      {availableSlots.map((slot) => (
                        <button
                          key={slot.time}
                          onClick={() => slot.available && updateFormData('appointmentTime', slot.time)}
                          disabled={!slot.available}
                          className={`
                            time-slot py-3 px-2 rounded-lg text-center font-medium text-sm
                            border transition-all
                            ${formData.appointmentTime === slot.time
                              ? 'bg-[#40E0D0] text-slate-900 border-[#40E0D0]'
                              : slot.available
                                ? 'bg-white border-slate-200 hover:border-[#40E0D0] text-slate-700'
                                : 'bg-slate-100 text-slate-400 border-slate-100 cursor-not-allowed'}
                          `}
                          data-testid={`timeslot-${slot.time}`}
                        >
                          {slot.time} Uhr
                        </button>
                      ))}
                    </div>
                    {availableSlots.length === 0 && (
                      <p className="text-center text-slate-500 py-4">
                        Keine verfügbaren Termine an diesem Tag
                      </p>
                    )}
                  </div>
                )}
                {errors.appointmentTime && <p className="text-red-500 text-sm mt-1">{errors.appointmentTime}</p>}
              </div>
            </div>

            {/* Selected appointment summary */}
            {formData.appointmentDate && formData.appointmentTime && (
              <div className="bg-[#E0F7FA] rounded-xl p-6 mt-6">
                <h4 className="font-semibold text-slate-900 mb-2">Ihr gewählter Termin:</h4>
                <p className="text-lg text-slate-700">
                  <CalendarIcon className="w-5 h-5 inline mr-2" />
                  {format(formData.appointmentDate, 'EEEE, dd. MMMM yyyy', { locale: de })} um {formData.appointmentTime} Uhr
                </p>
              </div>
            )}
          </div>
        )}

        {/* Step 4: Confirmation */}
        {currentStep === 4 && bookingComplete && (
          <div className="form-section text-center py-8" data-testid="step-confirmation">
            <div className="success-checkmark w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-white" />
            </div>
            
            <h3 className="font-['Merriweather'] text-2xl font-bold text-slate-900 mb-4">
              Termin erfolgreich gebucht!
            </h3>
            
            <p className="text-slate-600 text-lg mb-4 max-w-md mx-auto">
              Vielen Dank! Eine Bestätigung wurde an <strong>{formData.email}</strong> gesendet.
            </p>
            
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 max-w-md mx-auto mb-8">
              <p className="text-amber-800 text-sm">
                <strong>Hinweis:</strong> Bitte prüfen Sie auch Ihren Spam-Ordner, falls Sie keine E-Mail erhalten haben.
              </p>
            </div>

            <div className="bg-slate-50 rounded-xl p-6 max-w-md mx-auto text-left">
              <h4 className="font-semibold text-slate-900 mb-4">Ihre Buchungsdetails:</h4>
              
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <User className="w-5 h-5 text-[#40E0D0] mt-0.5" />
                  <div>
                    <p className="text-sm text-slate-500">Name</p>
                    <p className="font-medium text-slate-900">{formData.firstName} {formData.lastName}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <CalendarIcon className="w-5 h-5 text-[#40E0D0] mt-0.5" />
                  <div>
                    <p className="text-sm text-slate-500">Termin</p>
                    <p className="font-medium text-slate-900">
                      {formData.appointmentDate && format(formData.appointmentDate, 'dd.MM.yyyy', { locale: de })} um {formData.appointmentTime} Uhr
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-[#40E0D0] mt-0.5" />
                  <div>
                    <p className="text-sm text-slate-500">Rückrufnummer</p>
                    <p className="font-medium text-slate-900">{formData.phone}</p>
                  </div>
                </div>
              </div>
            </div>

            <p className="text-slate-500 mt-8">
              Wir werden Sie zur vereinbarten Zeit anrufen.
            </p>

            <Button
              onClick={resetForm}
              className="mt-6 bg-[#40E0D0] text-slate-900 hover:bg-[#3BCCC0] font-bold px-8 py-3 rounded-full"
              data-testid="btn-new-booking"
            >
              Neuen Termin buchen
            </Button>
          </div>
        )}

        {/* Navigation Buttons */}
        {!bookingComplete && (
          <div className="flex justify-between items-center mt-8 pt-6 border-t border-slate-100">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={currentStep === 1}
              className={`px-6 py-3 rounded-full ${currentStep === 1 ? 'invisible' : ''}`}
              data-testid="btn-back"
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Zurück
            </Button>

            {currentStep < 3 ? (
              <Button
                onClick={handleNext}
                className="bg-[#40E0D0] text-slate-900 hover:bg-[#3BCCC0] font-bold px-8 py-3 rounded-full"
                data-testid="btn-next"
              >
                Weiter
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            ) : currentStep === 3 ? (
              <Button
                onClick={handleSubmit}
                disabled={isLoading}
                className="bg-[#40E0D0] text-slate-900 hover:bg-[#3BCCC0] font-bold px-8 py-3 rounded-full"
                data-testid="btn-submit"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Wird gebucht...
                  </>
                ) : (
                  <>
                    Termin verbindlich buchen
                    <Check className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingWizard;
